/**
 * ContextualSimulator — carte compacte de repères dans la colonne
 * « Actions rapides » de la fiche bien (consultation interne).
 *
 * Lot CF — des RATIOS INCONTESTABLES, pas des projections. Les simulateurs n'amortissent plus
 * rien sur une durée supposée, n'affichent plus de « coût réel » reposant sur une hypothèse
 * implicite, et ne jugent plus un chiffre par une couleur. Aucun repère de marché chiffré n'est
 * écrit en dur : ces valeurs changent par secteur et par ville, et faisaient dire au logiciel
 * ce qu'il n'a pas les moyens de savoir.
 *
 * Comportement piloté par l'opération :
 *   - vente_immo (vente_murs)          → « Rendement & valorisation », DEUX blocs :
 *       · le bien au prix affiché : rendement brut = loyer annuel ÷ prix FAI
 *       · l'objectif de l'investisseur : prix à payer = loyer annuel ÷ rendement visé
 *       Les libellés suivent `vente.occupation_murs` : loyer « en place » et rendement
 *       « actuel » si occupé, loyer « de marché estimé » et rendement « potentiel » si libre.
 *
 *   - cession (vente_fonds)            → « Valorisation du fonds »
 *       · multiple prix / CA et prix / EBE (CA et EBE éditables)
 *
 *   - location                         → « Coût d'occupation »
 *       · coût annuel = loyer + charges + taxes, et son douzième
 *
 *   - location_droit_entree            → « Repères du pas-de-porte »
 *       · pas-de-porte en années de loyer · loyer annuel au m² · part du loyer dans le CA
 *
 *   - location_gerance                 → « Rentabilité de la gérance »
 *       · part du CA absorbée par la redevance
 *       · résultat estimé après redevance, sur un taux SAISI (vide par défaut)
 *
 *   - cession_bail / droit_au_bail     → « Repères du droit au bail »
 *       · prix en années de loyer · loyer annuel au m² · part du loyer dans le CA
 *
 *   - licence + toute autre valeur     → fallback « Simulation détaillée »
 *
 * Toute division dont un terme manque ou vaut 0 affiche « — », jamais 0 ni « NaN ».
 *
 * Design System Komerces :
 *   - carte blanche `bg-white rounded-2xl border border-slate-200 p-5`
 *   - titre Outfit, corps Inter, tabular-nums sur chiffres
 *   - primary blue `#007BFF` pour l'action, neutre partout ailleurs : ⛔ plus de couleur de
 *     jugement sur un résultat (le vert disait « bonne affaire », ce n'est pas notre rôle)
 *   - format FR partout via `formatPriceFR` / `formatPercentFR`, montants à l'euro
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatPriceFR, formatPercentFR } from '../../utils/formatFR';

/* ═══════════ CALCULS_CF_DEBUT — JS pur, éprouvé tel quel par les tests ═══════════ */
const toNum = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

// Une surface à 0 compte comme absente : le formulaire enregistre 0 quand le champ est vide.
const surfaceUtile = (v) => {
  const n = toNum(v);
  return n && n > 0 ? n : null;
};

// Un ratio n'a de sens que si ses deux termes existent et ne sont pas nuls.
const ratio = (numerateur, denominateur) => {
  const a = toNum(numerateur);
  const b = toNum(denominateur);
  if (!a || !b) return null;
  return a / b;
};

const anneesDeLoyer = (montant, loyerAnnuel) => ratio(montant, loyerAnnuel);
const loyerAuM2 = (loyerAnnuel, surface) => ratio(loyerAnnuel, surfaceUtile(surface));
const partDuCa = (loyerAnnuel, ca) => {
  const r = ratio(loyerAnnuel, ca);
  return r === null ? null : r * 100;
};
const rendementBrut = (loyerAnnuel, prix) => {
  const r = ratio(loyerAnnuel, prix);
  return r === null ? null : r * 100;
};
const prixPourTaux = (loyerAnnuel, tauxPct) => {
  const t = toNum(tauxPct);
  if (!t) return null;
  return ratio(loyerAnnuel, t / 100);
};

// « 2,7 ans » — une décimale, virgule française.
const formatAnnees = (valeur) => {
  const n = toNum(valeur);
  if (n === null) return '—';
  return `${n.toFixed(1).replace('.', ',')} ans`;
};
/* ═══════════ CALCULS_CF_FIN ═══════════ */

const MURS_OCCUPE = 'occupe';
const MURS_LIBRE = 'libre';
const NOTE_RENDEMENT = "Rendement brut = loyer annuel HT/HC ÷ prix FAI. Avant charges non refacturées, taxe foncière et frais d'acte.";
const NOTE_REPERES = 'Repères bruts, sans projection : à comparer aux valeurs de votre secteur.';

const HelperText = ({ children }) => (
  <p className="text-[11px] text-slate-500 mt-1 leading-snug">{children}</p>
);

const NoteCarte = ({ children }) => (
  <p className="text-[11px] text-slate-500 mt-3 pt-3 border-t border-slate-100 leading-snug" data-testid="ctx-sim-note">
    {children}
  </p>
);

// lot CF — plus de `tone` : un résultat ne se juge pas par une couleur.
const ResultRow = ({ label, value, hint, testId }) => (
  <div className="py-1.5">
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-sm text-slate-600">{label}</span>
      <span className="text-sm font-semibold tabular-nums whitespace-nowrap text-slate-900" data-testid={testId}>
        {value}
      </span>
    </div>
    {hint && <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>}
  </div>
);

/** Champ chiffre neutre (design system) */
const NumberField = ({ id, label, value, onChange, suffix, placeholder, helper, testId }) => (
  <div>
    <label htmlFor={id} className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">
      {label}
    </label>
    <div className="relative">
      <input
        id={id}
        type="number"
        inputMode="decimal"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full h-10 px-3 pr-10 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
      />
      {suffix && (
        <span className="absolute inset-y-0 right-3 flex items-center text-xs text-slate-400 pointer-events-none">
          {suffix}
        </span>
      )}
    </div>
    {helper && <HelperText>{helper}</HelperText>}
  </div>
);

/** Deux repères communs aux locations : loyer au m² et part du loyer dans le CA. */
const ReperesLoyer = ({ loyerAnnuel, surface, ca }) => {
  const auM2 = loyerAuM2(loyerAnnuel, surface);
  const part = partDuCa(loyerAnnuel, ca);
  return (
    <>
      <ResultRow
        label="Loyer annuel au m²"
        value={auM2 === null
          ? (surfaceUtile(surface) === null ? '— (surface non renseignée)' : '—')
          : `${formatPriceFR(auM2)}/m²/an`}
        hint="Loyer annuel HT/HC ÷ surface totale du bien."
        testId="ctx-sim-loyer-m2"
      />
      {toNum(ca) !== null && (
        <ResultRow
          label="Part du loyer dans le CA"
          value={formatPercentFR(part, { fallback: '—' })}
          hint="Loyer annuel HT/HC ÷ CA annuel HT du preneur."
          testId="ctx-sim-part-ca"
        />
      )}
    </>
  );
};

/** ═══════════════ Sous-composants par opération ═══════════════ */

/** Vente de murs : le bien au prix affiché, puis l'objectif de l'investisseur. */
const SimVenteImmo = ({ vente }) => {
  const prixCession = toNum(vente?.prix_cession) ?? toNum(vente?.prix_annonce);
  const loyerAnnuelInit = toNum(vente?.loyer_annuel_actuel)
    ?? (toNum(vente?.loyer_mensuel_actuel) !== null ? toNum(vente.loyer_mensuel_actuel) * 12 : null);

  const [loyerAnnuel, setLoyerAnnuel] = useState(loyerAnnuelInit ?? '');
  const [tauxVise, setTauxVise] = useState('7');

  // lot CF — murs occupés : le loyer est CONSTATÉ, le rendement est actuel. Murs libres : tout
  // part d'une estimation, et les libellés doivent le dire.
  const occupation = vente?.occupation_murs;
  const estLibre = occupation === MURS_LIBRE;
  const estOccupe = occupation === MURS_OCCUPE;

  const rendement = useMemo(() => rendementBrut(loyerAnnuel, prixCession), [loyerAnnuel, prixCession]);
  const prixCible = useMemo(() => prixPourTaux(loyerAnnuel, tauxVise), [loyerAnnuel, tauxVise]);
  const tauxLisible = formatPercentFR(toNum(tauxVise), { fallback: '—' });

  return (
    <div className="space-y-4" data-testid="ctx-sim-vente-immo" data-occupation={occupation || ''}>
      <div className="space-y-3" data-testid="ctx-sim-bloc-prix-affiche">
        <h4 className="text-xs font-bold tracking-wider uppercase text-slate-500">
          Le bien, au prix affiché
        </h4>
        <ResultRow
          label="Prix FAI, hors frais d'acte"
          value={formatPriceFR(prixCession, { fallback: '—' })}
          testId="ctx-vente-prix"
        />
        <NumberField
          id="ctx-vente-loyer"
          label={estOccupe ? 'Loyer annuel HT/HC en place'
            : estLibre ? 'Loyer de marché estimé (annuel HT/HC)'
              : 'Loyer annuel HT/HC'}
          value={loyerAnnuel}
          onChange={setLoyerAnnuel}
          suffix="€"
          placeholder="Loyer annuel du bail"
          helper="Loyer annuel hors taxes et hors charges perçu par le bailleur."
          testId="ctx-vente-loyer-input"
        />
        <ResultRow
          label={estLibre ? 'Rendement potentiel (hypothèse)' : 'Rendement brut actuel'}
          value={formatPercentFR(rendement, { fallback: '—' })}
          testId="ctx-vente-rendement"
        />
      </div>

      <div className="space-y-3 pt-3 border-t border-slate-100" data-testid="ctx-sim-bloc-objectif">
        <h4 className="text-xs font-bold tracking-wider uppercase text-slate-500">
          L&apos;objectif de l&apos;investisseur
        </h4>
        <NumberField
          id="ctx-vente-taux"
          label="Rendement visé"
          value={tauxVise}
          onChange={setTauxVise}
          suffix="%"
          placeholder="7"
          testId="ctx-vente-taux-input"
        />
        <ResultRow
          label={`Prix à payer pour obtenir ${tauxLisible}`}
          value={formatPriceFR(prixCible, { fallback: '—' })}
          hint={estLibre ? 'avec le loyer estimé' : estOccupe ? 'avec le loyer en place' : undefined}
          testId="ctx-vente-prix-cible"
        />
      </div>

      <NoteCarte>{NOTE_RENDEMENT}</NoteCarte>
    </div>
  );
};

/** Cession de fonds : multiples prix / CA et prix / EBE */
const SimCession = ({ vente, confid }) => {
  const prixCession = toNum(vente?.prix_cession) ?? toNum(vente?.prix_annonce);
  const [ca, setCa] = useState(toNum(confid?.chiffre_affaires_annuel) ?? '');
  const [ebe, setEbe] = useState(toNum(confid?.ebe) ?? '');

  const multipleCa = useMemo(() => ratio(prixCession, ca), [prixCession, ca]);
  const multipleEbe = useMemo(() => ratio(prixCession, ebe), [prixCession, ebe]);

  return (
    <div className="space-y-3" data-testid="ctx-sim-cession">
      <NumberField
        id="ctx-cession-ca"
        label="Chiffre d'affaires annuel"
        value={ca}
        onChange={setCa}
        suffix="€"
        placeholder="CA HT annuel"
        helper="CA HT annuel constaté sur le dernier exercice."
        testId="ctx-cession-ca-input"
      />
      <NumberField
        id="ctx-cession-ebe"
        label="EBE (Excédent Brut d'Exploitation)"
        value={ebe}
        onChange={setEbe}
        suffix="€"
        placeholder="EBE annuel"
        helper="Excédent brut d'exploitation : ce que l'activité dégage avant amortissements, provisions, frais financiers et impôt sur les bénéfices."
        testId="ctx-cession-ebe-input"
      />

      <div className="pt-3 border-t border-slate-100">
        <ResultRow
          label="Prix de cession FAI"
          value={formatPriceFR(prixCession, { fallback: '—' })}
          hint="Prix demandé pour la cession du fonds de commerce, honoraires d'agence inclus."
          testId="ctx-cession-prix"
        />
        <ResultRow
          label="Multiple prix / CA"
          value={multipleCa === null ? '—' : `× ${multipleCa.toFixed(2).replace('.', ',')}`}
          hint="Prix de cession FAI ÷ CA annuel HT."
          testId="ctx-cession-multiple-ca"
        />
        <ResultRow
          label="Multiple prix / EBE"
          value={multipleEbe === null ? '—' : `× ${multipleEbe.toFixed(2).replace('.', ',')}`}
          hint="Prix de cession FAI ÷ EBE."
          testId="ctx-cession-multiple-ebe"
        />
      </div>
    </div>
  );
};

/** Location : coût d'occupation (loyer + charges + taxes) */
const SimLocation = ({ bail }) => {
  const initLoyer = toNum(bail?.loyer_annuel)
    ?? (toNum(bail?.loyer_mensuel) !== null ? toNum(bail.loyer_mensuel) * 12 : null);
  const initCharges = toNum(bail?.charges_annuelles)
    ?? (toNum(bail?.charges) !== null ? toNum(bail.charges) * 12 : null)
    ?? (toNum(bail?.charges_mensuelles) !== null ? toNum(bail.charges_mensuelles) * 12 : null);
  const initTaxes = toNum(bail?.impot_foncier) ?? toNum(bail?.frais_annuels) ?? '';

  const [loyer, setLoyer] = useState(initLoyer ?? '');
  const [charges, setCharges] = useState(initCharges ?? '');
  const [taxes, setTaxes] = useState(initTaxes);

  const coutAnnuel = useMemo(() => {
    const parts = [toNum(loyer), toNum(charges), toNum(taxes)].filter((v) => v !== null);
    if (parts.length === 0) return null;
    return parts.reduce((s, v) => s + v, 0);
  }, [loyer, charges, taxes]);
  const coutMensuel = coutAnnuel === null ? null : coutAnnuel / 12;

  return (
    <div className="space-y-3" data-testid="ctx-sim-location">
      <NumberField
        id="ctx-loc-loyer"
        label="Loyer annuel HT/HC"
        value={loyer}
        onChange={setLoyer}
        suffix="€"
        placeholder="Loyer annuel"
        helper="Loyer annuel HT hors charges perçu par le bailleur (loyer_annuel = loyer_mensuel × 12)."
        testId="ctx-loc-loyer-input"
      />
      <NumberField
        id="ctx-loc-charges"
        label="Charges annuelles"
        value={charges}
        onChange={setCharges}
        suffix="€"
        placeholder="Charges annuelles"
        helper="Charges refacturées au locataire selon le bail."
        testId="ctx-loc-charges-input"
      />
      <NumberField
        id="ctx-loc-taxes"
        label="Taxes annuelles"
        value={taxes}
        onChange={setTaxes}
        suffix="€"
        placeholder="Taxe foncière, ordures ménagères…"
        helper="Taxes refacturées au preneur (foncière si prévue au bail, TEOM…)."
        testId="ctx-loc-taxes-input"
      />

      <div className="pt-3 border-t border-slate-100">
        <ResultRow
          label="Coût annuel total"
          value={formatPriceFR(coutAnnuel, { fallback: '—' })}
          hint="Loyer annuel + charges annuelles + taxes annuelles."
          testId="ctx-loc-cout-annuel"
        />
        <ResultRow
          label="Coût mensuel équivalent"
          value={formatPriceFR(coutMensuel, { fallback: '—' })}
          hint="Coût annuel total ÷ 12 (base pour comparer avec la trésorerie mensuelle)."
          testId="ctx-loc-cout-mensuel"
        />
      </div>
    </div>
  );
};

/** Location avec pas-de-porte : repères, sans amortissement supposé. */
const SimLocationDroitEntree = ({ bail, surface }) => {
  const initLoyer = toNum(bail?.loyer_annuel)
    ?? (toNum(bail?.loyer_mensuel) !== null ? toNum(bail.loyer_mensuel) * 12 : null);
  const initPdp = toNum(bail?.droit_entree);

  const [loyer, setLoyer] = useState(initLoyer ?? '');
  const [pdp, setPdp] = useState(initPdp ?? '');
  const [ca, setCa] = useState('');

  const annees = useMemo(() => anneesDeLoyer(pdp, loyer), [pdp, loyer]);

  return (
    <div className="space-y-3" data-testid="ctx-sim-location-droit-entree">
      <NumberField
        id="ctx-lde-loyer"
        label="Loyer annuel HT/HC"
        value={loyer}
        onChange={setLoyer}
        suffix="€"
        placeholder="Loyer annuel"
        helper="Loyer annuel HT hors charges. Distinct du pas-de-porte (versé une seule fois à l'entrée)."
        testId="ctx-lde-loyer-input"
      />
      <NumberField
        id="ctx-lde-pdp"
        label="Pas-de-porte"
        value={pdp}
        onChange={setPdp}
        suffix="€"
        placeholder="Droit d'entrée"
        helper="Somme versée au bailleur à l'entrée dans les lieux (irrécupérable en fin de bail)."
        testId="ctx-lde-pdp-input"
      />
      <NumberField
        id="ctx-lde-ca"
        label="CA annuel HT du preneur (facultatif)"
        value={ca}
        onChange={setCa}
        suffix="€"
        placeholder="CA HT annuel"
        testId="ctx-lde-ca-input"
      />

      <div className="pt-3 border-t border-slate-100">
        <ResultRow
          label="Pas-de-porte en années de loyer"
          value={formatAnnees(annees)}
          hint="Pas-de-porte ÷ loyer annuel HT/HC."
          testId="ctx-lde-annees"
        />
        <ReperesLoyer loyerAnnuel={loyer} surface={surface} ca={ca} />
      </div>
      <NoteCarte>{NOTE_REPERES}</NoteCarte>
    </div>
  );
};

/** Location-gérance : part du CA absorbée, puis résultat sur un taux SAISI. */
const SimLocationGerance = ({ bail, confid }) => {
  const initRedevance = toNum(bail?.redevance_annuelle)
    ?? (toNum(bail?.redevance_mensuelle) !== null ? toNum(bail.redevance_mensuelle) * 12 : null);
  const initCa = toNum(confid?.chiffre_affaires_annuel);

  const [redevance, setRedevance] = useState(initRedevance ?? '');
  const [ca, setCa] = useState(initCa ?? '');
  // lot CF — vide par défaut : un 15 préréglé passait pour une donnée du bien.
  const [taux, setTaux] = useState('');

  const pctCa = useMemo(() => partDuCa(redevance, ca), [redevance, ca]);

  const resultatApres = useMemo(() => {
    const c = toNum(ca);
    const t = toNum(taux);
    const r = toNum(redevance);
    if (c === null || t === null || r === null) return null;
    return c * (t / 100) - r;
  }, [ca, taux, redevance]);

  return (
    <div className="space-y-3" data-testid="ctx-sim-location-gerance">
      <NumberField
        id="ctx-lg-redevance"
        label="Redevance annuelle"
        value={redevance}
        onChange={setRedevance}
        suffix="€"
        placeholder="Redevance annuelle"
        helper="Redevance annuelle versée par le gérant au propriétaire du fonds."
        testId="ctx-lg-redevance-input"
      />
      <NumberField
        id="ctx-lg-ca"
        label="CA HT annuel"
        value={ca}
        onChange={setCa}
        suffix="€"
        placeholder="CA HT annuel"
        helper="CA HT annuel constaté ou prévu sur le fonds pris en gérance."
        testId="ctx-lg-ca-input"
      />
      <NumberField
        id="ctx-lg-marge"
        label="Taux de résultat avant redevance (estimé)"
        value={taux}
        onChange={setTaux}
        suffix="%"
        helper="Part du CA qui reste avant de payer la redevance. Hypothèse à saisir."
        testId="ctx-lg-marge-input"
      />

      <div className="pt-3 border-t border-slate-100">
        <ResultRow
          label="% du CA absorbé par la redevance"
          value={formatPercentFR(pctCa, { fallback: '—' })}
          hint="Redevance annuelle ÷ CA HT annuel."
          testId="ctx-lg-pct-ca"
        />
        <ResultRow
          label="Résultat estimé après redevance"
          value={formatPriceFR(resultatApres, { fallback: '—' })}
          hint="CA × taux de résultat avant redevance − redevance annuelle."
          testId="ctx-lg-resultat"
        />
      </div>
    </div>
  );
};

/** Cession de droit au bail : repères, sans durée restante supposée. */
const SimCessionBail = ({ vente, bail, surface }) => {
  const initPrix = toNum(vente?.prix_cession) ?? toNum(vente?.prix_annonce);
  const initLoyer = toNum(bail?.loyer_annuel)
    ?? (toNum(bail?.loyer_mensuel) !== null ? toNum(bail.loyer_mensuel) * 12 : null);

  const [prix, setPrix] = useState(initPrix ?? '');
  const [loyer, setLoyer] = useState(initLoyer ?? '');
  const [ca, setCa] = useState('');

  const annees = useMemo(() => anneesDeLoyer(prix, loyer), [prix, loyer]);

  return (
    <div className="space-y-3" data-testid="ctx-sim-cession-bail">
      <NumberField
        id="ctx-cb-prix"
        label="Prix du droit au bail"
        value={prix}
        onChange={setPrix}
        suffix="€"
        placeholder="Prix de cession"
        helper="Somme versée au cédant pour récupérer son bail commercial (droit au bail)."
        testId="ctx-cb-prix-input"
      />
      <NumberField
        id="ctx-cb-loyer"
        label="Loyer annuel HT/HC"
        value={loyer}
        onChange={setLoyer}
        suffix="€"
        placeholder="Loyer annuel"
        helper="Loyer annuel HT hors charges du bail repris."
        testId="ctx-cb-loyer-input"
      />
      <NumberField
        id="ctx-cb-ca"
        label="CA annuel HT du preneur (facultatif)"
        value={ca}
        onChange={setCa}
        suffix="€"
        placeholder="CA HT annuel"
        testId="ctx-cb-ca-input"
      />

      <div className="pt-3 border-t border-slate-100">
        <ResultRow
          label="Prix en années de loyer"
          value={formatAnnees(annees)}
          hint="Prix du droit au bail ÷ loyer annuel HT/HC."
          testId="ctx-cb-annees"
        />
        <ReperesLoyer loyerAnnuel={loyer} surface={surface} ca={ca} />
      </div>
      <NoteCarte>{NOTE_REPERES}</NoteCarte>
    </div>
  );
};

/** Fallback pour licence et toute opération non couverte */
const SimFallback = ({ operation, vente, bail }) => {
  const navigate = useNavigate();
  const prix = toNum(vente?.prix_cession) ?? toNum(vente?.prix_annonce) ?? '';
  const loyer = toNum(bail?.loyer_mensuel) ?? '';

  const goToCalculettes = () => {
    const params = new URLSearchParams();
    if (prix) params.set('prix', String(prix));
    if (loyer) params.set('loyer', String(loyer));
    navigate(`/outils/calculettes${params.toString() ? `?${params}` : ''}`);
  };

  return (
    <div data-testid="ctx-sim-fallback">
      <p className="text-sm text-slate-600 leading-relaxed">
        Cette opération n&apos;a pas encore de simulateur dédié dans la fiche. Utilisez la
        page complète pour projeter mensualités, rendement locatif ou frais d&apos;acquisition.
      </p>
      <button
        type="button"
        onClick={goToCalculettes}
        data-testid="ctx-sim-detail-link"
        className="mt-3 inline-flex items-center gap-2 h-10 px-4 bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/40 text-white rounded-lg text-sm font-semibold shadow-sm transition"
      >
        <i className="bi bi-calculator" />
        Simulation détaillée
        <i className="bi bi-arrow-right" />
      </button>
      <p className="text-[11px] text-slate-500 mt-2">
        Ouvre la page « Simulateurs » pré-remplie avec les données de ce bien.
      </p>
    </div>
  );
};

/** ═══════════════ Composant principal ═══════════════ */

const ContextualSimulator = ({ operation, informationsVente, informationsBail, informationsConfidentielles, surface }) => {
  const vente = informationsVente || {};
  const bail = informationsBail || {};
  const confid = informationsConfidentielles || {};

  // Alias legacy → nouveaux codes. Certaines fiches historiques utilisent
  // `vente_fonds` / `vente_murs` alors que la taxonomie canonique est
  // `cession` / `vente_immo`. `droit_au_bail` est un synonyme courant
  // de `cession_bail`. On normalise pour la logique du simulateur.
  const OP_ALIAS = {
    vente_fonds: 'cession',
    vente_murs: 'vente_immo',
    droit_au_bail: 'cession_bail',
  };
  const opNorm = OP_ALIAS[operation] || operation;

  const config = {
    vente_immo: {
      title: 'Rendement & valorisation',
      subtitle: 'Simulation contextuelle — vente de murs commerciaux',
      icon: 'bi-graph-up-arrow',
    },
    cession: {
      title: 'Valorisation du fonds',
      subtitle: 'Simulation contextuelle — cession de fonds de commerce',
      icon: 'bi-cash-stack',
    },
    location: {
      title: "Coût d'occupation",
      subtitle: 'Simulation contextuelle — location commerciale',
      icon: 'bi-house-door',
    },
    location_droit_entree: {
      title: 'Repères du pas-de-porte',
      subtitle: 'Simulation contextuelle — location avec droit d\'entrée',
      icon: 'bi-house-add',
    },
    location_gerance: {
      title: 'Rentabilité de la gérance',
      subtitle: 'Simulation contextuelle — location-gérance',
      icon: 'bi-briefcase',
    },
    cession_bail: {
      title: 'Repères du droit au bail',
      subtitle: 'Simulation contextuelle — cession de bail commercial',
      icon: 'bi-file-earmark-ruled',
    },
  }[opNorm] || {
    title: 'Simulateur',
    subtitle: 'Simulation contextuelle',
    icon: 'bi-calculator',
  };

  const HANDLED_OPS = ['vente_immo', 'cession', 'location', 'location_droit_entree', 'location_gerance', 'cession_bail'];

  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 p-5"
      data-testid="contextual-simulator"
      data-operation={opNorm || ''}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
          <i className={`bi ${config.icon} text-lg`} />
        </div>
        <div className="min-w-0">
          <h3
            className="text-sm font-bold text-slate-900 leading-tight"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {config.title}
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">{config.subtitle}</p>
        </div>
      </div>

      {opNorm === 'vente_immo' && <SimVenteImmo vente={vente} />}
      {opNorm === 'cession' && <SimCession vente={vente} confid={confid} />}
      {opNorm === 'location' && <SimLocation bail={bail} />}
      {opNorm === 'location_droit_entree' && <SimLocationDroitEntree bail={bail} surface={surface} />}
      {opNorm === 'location_gerance' && <SimLocationGerance bail={bail} confid={confid} />}
      {opNorm === 'cession_bail' && <SimCessionBail vente={vente} bail={bail} surface={surface} />}
      {!HANDLED_OPS.includes(opNorm) && (
        <SimFallback operation={opNorm} vente={vente} bail={bail} />
      )}
    </div>
  );
};

export default ContextualSimulator;
